/**
 * standStyleTokens.ts — Canonical named overlays for stand/feature visualization.
 *
 * Named overlay styles (selected, muted, risk, harvest, boundary) that are
 * composed from DS design tokens. Never use hex colors or opacity literals in
 * page code; always reference a named style via token-backed CSS variables.
 *
 * Each style is a semantic role, not a literal color pick. Roles can be
 * remapped globally by changing the underlying token without visiting every
 * call site.
 */

import type L from 'leaflet';

/**
 * Named stand/feature overlay styles, all token-backed.
 * Maps to CSS variables defined in tokens.css.
 */
export type StandOverlayName =
  | 'boundary'      // Neutral boundary; light stroke, low-opacity fill
  | 'selected'      // Accent halo that preserves the active semantic paint
  | 'muted'         // De-emphasized; gray, low opacity
  | 'risk'          // Risk or hazard; warm tones (orange/red spectrum)
  | 'harvest'       // Harvest-ready or operation planned; green/money tones
  | 'focus'         // Keyboard/mouse focus state; high contrast
  | 'untouched';    // Untouched/unmanaged stands; neutral fill

/**
 * Leaflet PathOptions for each stand overlay style.
 * ALL colors and opacity values are token-backed via CSS variables.
 *
 * Tokens (in tokens.css or design system):
 *  --fm-brand:              primary green
 *  --fm-accent:             secondary accent
 *  --fm-expected:           positive/ready green
 *  --fm-money:              financial/priority gold
 *  --fm-error:              risk/hazard red
 *  --fm-caution:            warning orange
 *  --fm-muted:              de-emphasis gray
 *  --fm-map-focus:          high-contrast focus ring
 *
 * DO NOT add new hex colors to this file. If you need a new tone,
 * add a semantic token to tokens.css and reference it here.
 */
export const standStyleTokens: Record<StandOverlayName, L.PathOptions> = {
  /**
   * boundary: Neutral stand boundary. Light stroke, minimal fill.
   * Use for: all-stands view, boundary reference.
   */
  boundary: {
    fillColor: 'var(--fm-accent-soft)',
    fillOpacity: 0.25,
    color: 'var(--fm-accent)',
    weight: 1.5,
    opacity: 0.7,
  },

  /**
   * selected: Accent halo that composes over the active semantic strategy.
   * Use for: selected stands without erasing species, volume, or maturity.
   */
  selected: {
    className: 'fm-stand-map__feature--selected',
    opacity: 1,
  },

  /**
   * muted: De-emphasized. Gray tones, low opacity.
   * Use for: background stands, non-target area, out-of-scope.
   */
  muted: {
    fillColor: 'var(--fm-d-fg-3)',
    fillOpacity: 0.15,
    color: 'var(--fm-d-fg-3)',
    weight: 1,
    opacity: 0.5,
  },

  /**
   * risk: Risk or hazard indicator. Warm tones.
   * Use for: hazard-affected stands, at-risk areas.
   */
  risk: {
    fillColor: 'var(--fm-error)',
    fillOpacity: 0.4,
    color: 'var(--fm-error)',
    weight: 2,
    opacity: 0.8,
    dashArray: '4, 2',
  },

  /**
   * harvest: Harvest-ready or operation scheduled. Positive green/money tones.
   * Use for: stands ready for harvest, planned operations.
   */
  harvest: {
    fillColor: 'var(--fm-expected)',
    fillOpacity: 0.45,
    color: 'var(--fm-expected)',
    weight: 2,
    opacity: 1,
  },

  /**
   * focus: Keyboard/mouse focus state. High contrast, thick ring.
   * Use for: focused stand in tables/maps, active selection.
   */
  focus: {
    fillColor: 'var(--fm-brand)',
    fillOpacity: 0.7,
    color: 'var(--fm-accent-strong)',
    weight: 3,
    opacity: 1,
  },

  /**
   * untouched: Unmanaged/untouched stands. Neutral fill.
   * Use for: passive background, no planned operations.
   */
  untouched: {
    fillColor: 'var(--fm-d-fg-3)',
    fillOpacity: 0.35,
    color: 'var(--fm-accent)',
    weight: 1.5,
    opacity: 0.6,
  },
};

/**
 * Get a stand overlay style by name.
 * Raises if style is unknown (fail-fast for typos).
 */
export function getStandOverlayStyle(name: StandOverlayName): L.PathOptions {
  const style = standStyleTokens[name];
  if (!style) {
    throw new Error(`Unknown stand overlay style: ${name}`);
  }
  return style;
}

/**
 * Merge two stand styles (for compound effects like selected + focus).
 * Later properties override earlier ones.
 */
export function mergeStandStyles(...styles: L.PathOptions[]): L.PathOptions {
  return Object.assign({}, ...styles);
}

/**
 * Apply opacity adjustment to a style (for hover, highlight effects).
 * Returns new PathOptions with adjusted fillOpacity/opacity.
 */
export function withOpacity(style: L.PathOptions, fillOpacity?: number, opacity?: number): L.PathOptions {
  return {
    ...style,
    ...(fillOpacity !== undefined && { fillOpacity }),
    ...(opacity !== undefined && { opacity }),
  };
}
